// Copyright (c) Docugami, Inc. All rights reserved.

import { TooltipHost } from "@fluentui/react";
import { dgFontFamily } from "components/common/Fonts";
import { lightButtonGray, backgroundGray, primaryAccentColor, homeGray } from "components/shell/presentation/AppStyles";
import { ReactNode } from "react";
import styled from "styled-components";
import { addAutomationIdCompletePart } from "utils/Automation";

export enum IndicatorTooltipVisibility {
    Always,
    Complete,
    Incomplete
}

export interface IndicatorTooltipProps {
    tooltip: ReactNode;
    visibility?: IndicatorTooltipVisibility;
}

export interface IndicatorDetails {
    label: string;
    tooltipProps?: IndicatorTooltipProps;
    automationId?: string;
}

const indicatorBubbleZIndex = 2;
const indicatorZIndex = 1;

const Label = styled.span<{complete?: boolean}>`
    font-family: ${dgFontFamily};
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: center;

    position: absolute;
    bottom: -30px;
    width: 2.5em;
    color: ${lightButtonGray};
    text-wrap: nowrap;
    display: flex;
    justify-content: center;

    ${props => !!props.complete && `color: ${homeGray};`};
`;

const BubbleProgressBarRoot = styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;

    width: 500px;
    animation: widthTransition 1s ease-in-out;

    ::before {
        position: absolute;
        content: '';
        top: 50%;
        height: 5px;
        width: 100%;
        background-color: ${lightButtonGray};
        z-index: ${indicatorZIndex};
    }
`;

const BubbleRoot = styled.div`
    position: relative;
    z-index: ${indicatorBubbleZIndex};
`;

const Bubble = styled.div<{complete?: boolean}>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5em;
    height: 2.5em;
    border-radius: 50%;
    border: 5px solid ${lightButtonGray};
    box-sizing: border-box;
    background-color: ${backgroundGray};
    z-index: 2;
    color: ${lightButtonGray};

    ${props => !!props.complete && `background-color: ${primaryAccentColor}`};
`;

interface FormProgressBarProps {
    indicators: IndicatorDetails[];
    completedUntilIndex?: number;
}

export const BubbleProgressBar = (props: FormProgressBarProps): JSX.Element => {
    return (
        <BubbleProgressBarRoot>
            {
               props.indicators.map((x, index) => {
                    const complete = props.completedUntilIndex !== undefined && index <= props.completedUntilIndex;

                    return (
                        <TooltipHost key={`tooltip_${x.label}`} content={getIndicatorTooltipContent( x.tooltipProps, complete)}>
                            <BubbleRoot
                                key={x.label} 
                                data-automation-id={!!x.automationId ? addAutomationIdCompletePart(x.automationId, complete) : undefined}>
                                <Bubble complete={complete} />
                                <Label complete={complete}>{x.label}</Label>
                            </BubbleRoot>
                        </TooltipHost>
                    )
                })
            }
        </BubbleProgressBarRoot>
    )
};

function getIndicatorTooltipContent(tooltipProps: IndicatorTooltipProps | undefined, complete: boolean) {
    if (!tooltipProps) {
        return;
    }

    const tooltipContent = <>{tooltipProps.tooltip}</>

    if (tooltipProps.visibility === IndicatorTooltipVisibility.Complete) {
        return complete ? tooltipContent : undefined;
    } else if (tooltipProps.visibility === IndicatorTooltipVisibility.Incomplete) {
        return !complete ? tooltipContent : undefined;
    } else {
        return tooltipContent;
    }
}